import { useState } from 'react';

import { useQueryClient } from 'react-query';
import { isStringHasRealContent } from 'utilitys/helpers/general';

import { DEFAULT_ERROR, useCustomSnackBar } from 'hooks/useCustomSnackBar';
import useEventWithDimensions from 'hooks/useEventWithDimensions';
import useUserHook from 'hooks/useUserHook';

import { ReportComment } from 'api/endPoints/getReportComments';
import { useMutationCreateEditReportComment } from 'api/mutations/createEditReportComment.mutation';
import { useMutationDeleteReportComment } from 'api/mutations/deleteReportComment.mutation';
import { useGetReportComments } from 'api/queries/getReportComments';

interface Props {
    reportId: number;
    queryKeys: string[];
}

interface CommentData {
    reportId?: number | null;
    commentMessage: string;
    mentionedList: string[];
    id?: number | null;
    commentId?: number | null;
    error: boolean;
    commentIdInEditMode?: number | null;
    commentIdToRemove: number | null;
}

const sortCommentsData = (arr) => {
    return arr.sort(function (a, b) {
        return -(a.commentId - b.commentId);
    });
};

export const useReportCommentProvider = ({ reportId, queryKeys }: Props) => {
    const queryclient = useQueryClient();
    const { firstName, lastName, username, email, image } = useUserHook();
    const [deleteCommentModalShow, setDeleteCommentModalShow] = useState(false);

    const [commentData, setCommentData] = useState<CommentData>({
        commentMessage: '',
        mentionedList: [],
        error: false,
        commentIdToRemove: null,
    });
    const [commentsEditorStatus, setCommentsEditorStatus] = useState({
        isOpen: false,
        isError: false,
    });

    const { handleSnackBar } = useCustomSnackBar();
    const { sendEventWithDimensions } = useEventWithDimensions();
    const { data: reportCommentsData, isFetching: isFetchingReportComments } = useGetReportComments({ reportId });
    const { mutate: createEditCommentMutate, isLoading: displayCreateCommentLoader } =
        useMutationCreateEditReportComment();
    const { mutate: deleteCommentMutate } = useMutationDeleteReportComment();

    const setAddCommentMode = (value: string) => {
        setCommentData({
            ...commentData,
            reportId,
            id: null,
            commentMessage: value,
            commentId: null,
            error: false,
            commentIdInEditMode: null,
        });

        setCommentsEditorStatus({
            isOpen: true,
            isError: false,
        });
    };

    const cancelAddCommentMode = () => {
        setCommentData({ ...commentData, commentMessage: '', mentionedList: [] });
        setCommentsEditorStatus({
            isOpen: false,
            isError: false,
        });
    };

    const handleEditorDescription = (value, mentionedList) => {
        setCommentData({ ...commentData, commentMessage: value, mentionedList });
    };

    const createComment = () => {
        if (isStringHasRealContent(commentData.commentMessage)) {
            setCommentData({ ...commentData, error: true });
            return;
        }

        createEditCommentMutate(
            {
                id: null,
                reportId,
                comment: commentData.commentMessage,
                mentionedList: commentData.mentionedList,
            },
            {
                onSuccess: (res) => {
                    sendEventWithDimensions({
                        category: 'Reporting',
                        action: 'Comment Added',
                        label: 'None',
                    });
                    handleSnackBar('Comment Successfully Saved', 'success');

                    return Promise.all([
                        ...queryKeys.map((key) => queryclient.invalidateQueries(key)),
                        queryclient.invalidateQueries('getReportComments'),
                    ]).then(() => {
                        setCommentsEditorStatus({
                            isOpen: false,
                            isError: false,
                        });
                        setCommentData({ ...commentData, reportId: res.reportId || null, commentMessage: '' });
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );

        sendEventWithDimensions({
            category: 'Report Comments',
            action: 'Add Comment',
            label: String(commentData.reportId),
        });
    };

    const updateComment = (commentId) => {
        if (isStringHasRealContent(commentData.commentMessage)) {
            setCommentData({ ...commentData, error: true });
            return;
        }

        createEditCommentMutate(
            {
                id: commentId,
                reportId,
                comment: commentData.commentMessage,
                mentionedList: commentData.mentionedList,
            },
            {
                onSuccess: (res) => {
                    handleSnackBar('Comment Successfully Updated', 'success');

                    return Promise.all([
                        ...queryKeys.map((key) => queryclient.invalidateQueries(key)),
                        queryclient.invalidateQueries('getReportComments'),
                    ]).then(() => {
                        setCommentsEditorStatus({
                            isOpen: false,
                            isError: false,
                        });
                        setCommentData({
                            ...commentData,
                            reportId: res.reportId || null,
                            commentMessage: '',
                            commentIdInEditMode: null,
                        });
                    });
                },
                onError: (error) => {
                    DEFAULT_ERROR({ error, handleError: handleSnackBar });
                },
            },
        );

        sendEventWithDimensions({
            category: 'Update Report Comments',
            action: 'Update Comment',
            label: reportId.toString(),
        });
    };

    const confirmDeleteComment = (id: number) => {
        setCommentData({ ...commentData, commentIdToRemove: id });
        setDeleteCommentModalShow(true);
    };

    const handleCommentDelete = (id: number) => {
        setDeleteCommentModalShow(false);
        deleteCommentMutate(id, {
            onSuccess: () => {
                handleSnackBar('Comment Successfully Deleted', 'success');

                return Promise.all([
                    ...queryKeys.map((key) => queryclient.invalidateQueries(key)),
                    queryclient.invalidateQueries('getReportComments'),
                ]);
            },
            onError: (error) => {
                DEFAULT_ERROR({ error, handleError: handleSnackBar });
            },
        });
    };

    const sortedComments: ReportComment[] = reportCommentsData ? sortCommentsData([...reportCommentsData]) : [];

    const showLoader = displayCreateCommentLoader || isFetchingReportComments;
    return {
        cancelAddCommentMode,
        commentData,
        commentsEditorStatus,
        confirmDeleteComment,
        createComment,
        deleteCommentModalShow,
        email,
        firstName,
        handleCommentDelete,
        handleEditorDescription,
        image,
        lastName,
        setAddCommentMode,
        setCommentData,
        setDeleteCommentModalShow,
        sortedComments,
        updateComment,
        username,
        showLoader,
        setCommentsEditorStatus,
        isFetchingReportComments,
    };
};
